import { useEffect } from "react";
import { Phone, MessageCircle, ShieldCheck, TrendingUp, Award, Target, BarChart3, Search, MapPin, Youtube, LayoutGrid, AlertTriangle, CheckCircle2, XCircle, Clock, Sparkles, ArrowRight, Quote, Star } from "lucide-react";
import profileAsset from "@/assets/dijital-mustafa-profil.png";
import googleAdsLogo from "@/assets/google-ads-logo.png";

const PHONE_DISPLAY = "0532 636 91 15";
const PHONE_TEL = "05326369115";
const WA_NUMBER = "905326369115";
const WA_MSG = encodeURIComponent("Merhaba Dijital Mustafa, Google reklamlarım için ücretsiz ön görüşme almak istiyorum.");
const PARTNER_URL = "https://www.google.com/partners/agency?id=6479203352";
const CANONICAL = "https://www.reklam-uzmani.com.tr/google-reklamlari-yonetimi";

const GoogleReklamlariYonetimi = () => {
  useEffect(() => {
    document.title = "Google Reklam Yönetimi — Dijital Mustafa | Google Partner";
    const desc = document.querySelector('meta[name="description"]');
    const content = "18 yıllık tecrübeyle Google reklamlarınızı sertifikalı Google Partner olarak yönetiyorum. Bütçenizi koruyan, dönüşüm odaklı performans yönetimi.";
    if (desc) desc.setAttribute("content", content);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = CANONICAL;

    // JSON-LD
    const ldId = "ld-google-reklamlari-yonetimi";
    document.getElementById(ldId)?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Dijital Mustafa — Google Reklam Yönetimi",
      url: CANONICAL,
      image: `https://www.reklam-uzmani.com.tr${profileAsset}`,
      telephone: "+90 532 636 91 15",
      areaServed: "TR",
      priceRange: "₺₺",
      description: content,
      sameAs: [PARTNER_URL],
    });
    document.head.appendChild(ld);

    return () => {
      document.getElementById(ldId)?.remove();
    };
  }, []);

  const waHref = `https://wa.me/${WA_NUMBER}?text=${WA_MSG}`;
  const telHref = `tel:${PHONE_TEL}`;

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#04221a] via-[#053b2c] to-[#066a4c] pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-emerald-300 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Profile */}
            <div className="lg:col-span-5 order-1 lg:order-1 flex justify-center lg:justify-start">
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-400/30 blur-3xl rounded-full" />
                <img
                  src={profileAsset}
                  alt="Dijital Mustafa — Sertifikalı Google Partner"
                  width={520}
                  height={690}
                  loading="eager"
                  className="relative w-[280px] sm:w-[340px] md:w-[400px] lg:w-[440px] h-auto rounded-3xl shadow-2xl ring-1 ring-emerald-300/30"
                />
                <a
                  href={PARTNER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute -bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-xl border-2 border-emerald-500 text-emerald-700 font-bold text-sm hover:scale-105 transition-transform"
                >
                  <Award className="w-4 h-4" />
                  Sertifikalı Google Partner
                </a>
              </div>
            </div>

            {/* Copy */}
            <div className="lg:col-span-7 order-2 lg:order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/95 border border-white rounded-full text-gray-900 text-xs md:text-sm font-bold mb-5 shadow-lg">
                <img src={googleAdsLogo} alt="Google Ads" width={20} height={20} className="w-5 h-5" />
                Sertifikalı Google Partner · 18 Yıllık Deneyim
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-5">
                Google Reklamlarınızı{" "}
                <span className="text-emerald-300">Bizzat Ben Yönetiyorum</span>
              </h1>

              <p className="text-base md:text-lg text-emerald-50/90 max-w-2xl mx-auto lg:mx-0 mb-7 leading-relaxed">
                Ben <strong className="text-white">Dijital Mustafa</strong>. Sertifikalı Google Partner olarak işletmelerin Google reklam hesaplarını profesyonelce yönetiyor, sahte tıklamalardan koruyor ve bütçenizi gerçek müşteriye dönüştürüyorum.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-6">
                <a
                  href={telHref}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-base md:text-lg shadow-lg shadow-red-900/40 hover:scale-[1.02] transition-all"
                >
                  <Phone className="w-5 h-5" />
                  Hemen Ara: {PHONE_DISPLAY}
                </a>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#25D366] hover:bg-[#1eb858] text-white rounded-xl font-bold text-base md:text-lg shadow-lg shadow-green-900/40 hover:scale-[1.02] transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp ile Yaz
                </a>
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-emerald-100/80">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> 15 dk ücretsiz ön görüşme</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Sözleşmesiz başlangıç</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Şeffaf raporlama</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE ADS BRAND STRIP */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-7">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-center md:text-left">
            <img src={googleAdsLogo} alt="Google Ads Logosu" width={56} height={56} className="w-12 h-12 md:w-14 md:h-14" />
            <div className="md:border-l-2 md:border-gray-200 md:pl-6">
              <p className="text-xs md:text-sm uppercase tracking-wider text-gray-500 font-bold">Resmi İş Ortağı</p>
              <p className="text-base md:text-lg font-black text-gray-900">Google Sertifikalı Partner</p>
            </div>
            <a href={PARTNER_URL} target="_blank" rel="noopener noreferrer" className="md:ml-auto inline-flex items-center gap-2 text-emerald-700 hover:text-emerald-900 font-bold text-sm">
              Partner profilimi doğrula <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
            {[
              { v: "18+", l: "Yıllık Google Reklam Tecrübesi" },
              { v: "500+", l: "Yönetilen Google Reklam Kampanyası" },
              { v: "%80", l: "Sahte Tıklama Koruması" },
              { v: "Partner", l: "Sertifikalı Google Partner" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-2xl md:text-4xl font-black text-emerald-700">{s.v}</div>
                <div className="text-xs md:text-sm text-gray-600 font-semibold mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM — SAHTE TIKLAMA */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-full text-red-700 text-sm font-bold mb-4">
              <AlertTriangle className="w-4 h-4" />
              Çoğu işletmenin haberi bile yok
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">
              Reklam Bütçenizin <span className="text-red-600">%40'a Yakını</span> Sahte Tıklamalara Gidiyor Olabilir
            </h2>
            <p className="text-lg text-gray-600">
              Rakip tıklamaları, bot trafiği ve sahte ziyaretler — Google'ın varsayılan filtreleri bunların büyük kısmını yakalamaz. Sonuç: bütçe biter, telefon çalmaz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { i: AlertTriangle, t: "Rakip Saldırıları", d: "Rakipler reklamlarınıza tıklayarak bütçenizi tüketiyor; siz reklam yayında sanıyorsunuz." },
              { i: AlertTriangle, t: "Bot Trafiği", d: "Otomatik botlar sahte tıklama üretiyor; raporlarda 'tıklama' görünür ama gerçek ziyaretçi yok." },
              { i: AlertTriangle, t: "Görünmez Bütçe Kaybı", d: "Sahte tıklamaların maliyeti aylık binlerce TL'yi bulabilir — ve standart panelde fark edilmez." },
            ].map((c) => (
              <div key={c.t} className="bg-white p-6 rounded-2xl border-2 border-red-100 hover:border-red-300 hover:shadow-lg transition-all">
                <c.i className="w-8 h-8 text-red-600 mb-3" />
                <h3 className="font-black text-gray-900 mb-2 text-lg">{c.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#04221a] to-[#053b2c]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-white mb-3">
              Profesyonel Yönetim <span className="text-emerald-300">Öncesi & Sonrası</span>
            </h2>
            <p className="text-emerald-100/80 max-w-2xl mx-auto">
              Aynı bütçe, aynı sektör — fark sadece kampanyaları kimin yönettiği.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ÖNCESİ */}
            <div className="relative bg-white rounded-3xl p-7 border-t-8 border-red-500 shadow-xl">
              <div className="absolute -top-4 left-7 px-4 py-1 bg-red-500 text-white rounded-full text-xs font-black tracking-wider">ÖNCESİ</div>
              <h3 className="text-2xl font-black text-gray-900 mt-3 mb-5">Amatör / Otomatik Yönetim</h3>
              <ul className="space-y-3 text-gray-700">
                {[
                  "Bütçenin %35-40'ı sahte tıklamalara gidiyor",
                  "Aylık 15-20 dönüşüm, yüksek tıklama maliyeti",
                  "Anahtar kelime listesi denetlenmiyor",
                  "Negatif kelime havuzu boş veya eksik",
                  "Şeffaf rapor yok, sadece Google paneli",
                  "Dönüşüm değeri ölçülmüyor",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* SONRASI */}
            <div className="relative bg-white rounded-3xl p-7 border-t-8 border-emerald-500 shadow-xl">
              <div className="absolute -top-4 left-7 px-4 py-1 bg-emerald-500 text-white rounded-full text-xs font-black tracking-wider">SONRASI</div>
              <h3 className="text-2xl font-black text-gray-900 mt-3 mb-5">Dijital Mustafa Yönetimi</h3>
              <ul className="space-y-3 text-gray-700">
                {[
                  "Sahte tıklamaların %80'i otomatik olarak engelleniyor",
                  "Aylık 60-90 dönüşüm, düşen tıklama maliyeti",
                  "Her hafta anahtar kelime denetimi",
                  "Geniş negatif kelime havuzu, sürekli güncel",
                  "Sade & şeffaf aylık performans raporu",
                  "Her dönüşüme TL değeri atanıyor, ROAS net",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NEDEN BEN */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3">Neden Dijital Mustafa?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Çağrı merkezi değil, ekiple paslanan dosya değil — kampanyalarınızı tek bir kişi, baştan sona ben yönetiyorum.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Award, t: "Google Partner Sertifikası", d: "Resmi Google Partner profili ile doğrulanmış yetkinlik." , link: true },
              { i: Clock, t: "18 Yıllık Sektör Tecrübesi", d: "2007'den bu yana yalnızca Google reklamları üzerine çalışıyorum." },
              { i: ShieldCheck, t: "Sahte Tıklama Kalkanı", d: "%80'e varan tıklama dolandırıcılığı koruması ile bütçeniz güvende." },
              { i: TrendingUp, t: "ROAS Odaklı Yönetim", d: "Tıklama değil, getiri optimize ediyorum. Her TL hesaplanabilir." },
            ].map((c) => (
              <div key={c.t} className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-emerald-400 hover:shadow-xl transition-all bg-white">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <c.i className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-black text-gray-900 mb-2">{c.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{c.d}</p>
                {c.link && (
                  <a
                    href={PARTNER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-emerald-700 hover:text-emerald-900"
                  >
                    Partner profilim <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HİZMET ÇERÇEVEM */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3">Hangi Google Reklamlarını Yönetiyorum?</h2>
            <p className="text-gray-600">Tüm Google reklam ağlarında uçtan uca yönetim.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { i: Search, t: "Arama Ağı" },
              { i: LayoutGrid, t: "Performance Max" },
              { i: Youtube, t: "YouTube" },
              { i: MapPin, t: "Google Haritalar" },
              { i: Target, t: "Display & Remarketing" },
            ].map((s) => (
              <div key={s.t} className="bg-white p-5 rounded-2xl border border-gray-200 text-center hover:border-emerald-400 hover:shadow-md transition-all">
                <s.i className="w-7 h-7 text-emerald-600 mx-auto mb-2" />
                <p className="font-bold text-gray-900 text-sm">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE PARTNER BÖLÜM */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-white via-emerald-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl border-2 border-emerald-200 p-8 md:p-12 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-white shadow-xl border border-gray-100 mb-5 p-3">
              <img src={googleAdsLogo} alt="Google Ads" width={72} height={72} className="w-full h-full object-contain" />
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
              Sertifikalı <span className="text-emerald-700">Google Partner</span> Profilim
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-7 leading-relaxed">
              Google Partner statüsü; sertifikalandırılmış bilgi seviyesi, kanıtlanmış reklam performansı ve sürdürülebilir müşteri başarısı şartlarını karşılayanlara verilir. Profilimi resmi Google sayfasından doğrulayabilirsiniz.
            </p>
            <a
              href={PARTNER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold shadow-lg shadow-emerald-900/30 hover:scale-[1.02] transition-all"
            >
              <Award className="w-5 h-5" />
              Google Partner Profilimi Görüntüle
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SÜREÇ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3">4 Adımda Çalışıyoruz</h2>
            <p className="text-gray-600">Net, şeffaf, sürpriz yok.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Ücretsiz Analiz", d: "15 dk görüşmede mevcut hesabınızı ve rakip pazarınızı inceliyorum." },
              { n: "02", t: "Strateji", d: "Sektörünüze, hedefinize ve bütçenize özel kampanya yapısı planlıyorum." },
              { n: "03", t: "Kurulum & Yayın", d: "Dönüşüm izleme, negatif kelimeler, sahte tıklama koruması — eksiksiz." },
              { n: "04", t: "Sürekli Optimizasyon", d: "Haftalık iyileştirme, aylık şeffaf rapor, anlık WhatsApp iletişimi." },
            ].map((s) => (
              <div key={s.n} className="relative p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-100">
                <div className="text-5xl font-black text-emerald-200 leading-none mb-2">{s.n}</div>
                <h3 className="font-black text-gray-900 mb-2 text-lg">{s.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MİNİ REFERANSLAR */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-3">Ölçülebilir Sonuçlar</h2>
            <p className="text-gray-600">Müşterilerimin gerçek performans verileri.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { s: "Hukuk Bürosu — İstanbul", b: "3.2x", l: "ROAS artışı (3 ay)" },
              { s: "E-ticaret — Tekstil", b: "−%62", l: "Tıklama maliyeti düşüşü" },
              { s: "Yerel Klinik — Ankara", b: "+184%", l: "Aylık randevu sayısı" },
            ].map((c) => (
              <div key={c.s} className="bg-white p-7 rounded-2xl border border-gray-200 shadow-sm">
                <div className="text-4xl font-black text-emerald-700 mb-1">{c.b}</div>
                <div className="text-sm font-semibold text-gray-700 mb-3">{c.l}</div>
                <div className="text-xs text-gray-500 border-t pt-3">{c.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KİŞİSEL İMZA BANDI */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-[#04221a] via-[#053b2c] to-[#066a4c] rounded-3xl overflow-hidden shadow-2xl">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl pointer-events-none" />
            <img src={googleAdsLogo} alt="" aria-hidden="true" className="absolute top-6 right-6 w-16 h-16 md:w-20 md:h-20 opacity-90" width={80} height={80} />

            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center p-8 md:p-12">
              <div className="md:col-span-4 flex justify-center">
                <div className="relative">
                  <div className="absolute -inset-3 bg-emerald-400/30 blur-2xl rounded-full" />
                  <img
                    src={profileAsset}
                    alt="Dijital Mustafa — Google Ads Partner"
                    width={300}
                    height={400}
                    loading="lazy"
                    className="relative w-44 md:w-56 h-auto rounded-2xl shadow-2xl ring-2 ring-emerald-300/40 object-cover"
                  />
                </div>
              </div>

              <div className="md:col-span-8 text-white">
                <Quote className="w-10 h-10 text-emerald-300 mb-3" />
                <p className="text-xl md:text-2xl font-bold leading-relaxed mb-5">
                  "Google Reklam hesabınıza ben bakıyorum — asistan değil, stajyer değil, bot değil. 18 yıllık tecrübemle <span className="text-emerald-300">her TL'nizin gerçek müşteriye dönüşmesi</span> tek hedefim."
                </p>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="font-black text-lg">Dijital Mustafa</div>
                    <div className="text-emerald-200/80 text-sm">Sertifikalı Google Partner · İstanbul</div>
                  </div>
                  <div className="ml-auto flex items-center gap-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SSS */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-8 text-center">Sıkça Sorulanlar</h2>
          <div className="space-y-3">
            {[
              { q: "Minimum reklam bütçesi ne kadar olmalı?", a: "Sektöre göre değişmekle birlikte, aylık reklam bütçesinin sağlıklı bir test için en az 5.000 TL olmasını öneriyorum." },
              { q: "Sözleşme zorunluluğu var mı?", a: "Hayır. İlk ay deneme dönemidir; performanstan memnun değilseniz devam zorunluluğunuz yoktur." },
              { q: "Raporlamayı nasıl yapıyorsunuz?", a: "Aylık sade ve anlaşılır PDF rapor + istediğiniz zaman canlı dashboard erişimi sağlıyorum." },
              { q: "Sahte tıklama korumasını nasıl uyguluyorsunuz?", a: "Profesyonel tıklama dolandırıcılığı yazılımı + manuel IP analizi + sürekli negatif yerleşim güncellemesi kombinasyonu kullanıyorum." },
              { q: "Hangi sektörlerde çalışıyorsunuz?", a: "Hukuk, sağlık, e-ticaret, eğitim, B2B hizmet, yerel işletmeler ve daha fazlası. 18 yılda 30+ sektör tecrübem var." },
            ].map((f) => (
              <details key={f.q} className="group bg-gray-50 rounded-xl border border-gray-200 hover:border-emerald-300 transition-colors">
                <summary className="flex items-center justify-between cursor-pointer p-5 font-bold text-gray-900">
                  {f.q}
                  <span className="text-emerald-600 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <div className="px-5 pb-5 text-gray-700 text-sm leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#04221a] via-[#053b2c] to-[#066a4c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-emerald-300 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white text-xs font-bold mb-6">
            <Clock className="w-4 h-4" />
            Bu ay kontenjan: 5 yeni firma
          </div>

          <img src={googleAdsLogo} alt="Google Ads" width={64} height={64} className="w-14 h-14 mx-auto mb-4" />

          <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            Google Reklam Hesabınız İçin 15 Dakikalık Ücretsiz Görüşme
          </h2>
          <p className="text-emerald-50/90 text-lg mb-8 max-w-2xl mx-auto">
            Hesabınızı bizzat ben inceliyorum, sahte tıklama oranınızı tespit ediyorum ve size özel bir yol haritası sunuyorum — tamamen ücretsiz.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={telHref}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-black text-lg shadow-2xl shadow-red-900/40 hover:scale-[1.02] transition-all"
            >
              <Phone className="w-6 h-6" />
              {PHONE_DISPLAY}
            </a>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-5 bg-[#25D366] hover:bg-[#1eb858] text-white rounded-xl font-black text-lg shadow-2xl shadow-green-900/40 hover:scale-[1.02] transition-all"
            >
              <MessageCircle className="w-6 h-6" />
              WhatsApp'tan Yaz
            </a>
          </div>

          <p className="mt-6 text-sm text-emerald-200/70">
            <a href={PARTNER_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              Google Partner profilimi doğrulayın
            </a>
            {" · Türkiye geneli hizmet"}
          </p>
        </div>
      </section>

      {/* STICKY MOBILE/DESKTOP CALL BUTTON */}
      <div className="fixed bottom-4 right-4 z-[80] flex flex-col gap-2">
        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp"
          className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
        <a
          href={telHref}
          aria-label="Telefonla ara"
          className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center shadow-2xl hover:scale-110 transition-transform animate-pulse"
        >
          <Phone className="w-6 h-6" />
        </a>
      </div>
    </div>
  );
};

export default GoogleReklamlariYonetimi;
